// ** Router Import
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { loginRequest } from '../authConfig'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Router from '../router/Router'
import mapper from '../_endpoints'
import { setUserContext } from '../_redux/actions/userContextActions'
import { useFetch } from '../_hooks/useFetch'
import { useNavigate, useLocation } from 'react-router-dom'
import { ai, appInsights } from '../_services/AppInsights'
import { DefaultRoute, MainRoute } from './routes'
import { useNotificationService } from '../_services/NotificationService'
import { getConfig } from '../configs/config'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { EventType } from '../utility/types'
import { setWorkspaceData } from '../_redux/actions/workspaceActions'
import { useWorkspaceService } from '../_services/WorkspaceService'
import { SplashScreen } from '../SplashScreen'

const RoutesManager = () => {
  const { instance, inProgress, accounts } = useMsal()
  const config = getConfig()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const context = useSelector((state: any) => state.userContextReducer.userContext)
  const [isUserDetailsLoading, getUserDetailsData] = useFetch()
  const [isProfileDataLoading, getProfileUserData] = useFetch()
  const [isworkspaceConfigLoading, getWorkspaceConfig] = useFetch()
  const [isInInvestmentDesktop, setIsInInvestmentDesktop] = useState()
  const { connectSignalR } = useNotificationService()
  //@ts-ignore
  const trackPortalVisit = useTrackEvent(appInsights, EventType.PortalVisit)
  const searchParams = new URLSearchParams(location?.search)
  const source = searchParams.get('source')
  const { workspaces, activeWorkspace } = useSelector((state: any) => state.workspaceReducer)
  const { setUpNewWorkspace } = useWorkspaceService()

  const isRoutesManagerLoading =
    isUserDetailsLoading || isProfileDataLoading || isworkspaceConfigLoading

  useEffect(() => {
    if (source !== null) {
      trackPortalVisit({ source })
    }
  }, [source])

  useEffect(() => {
    if (accounts && inProgress === InteractionStatus.None) {
      const request = {
        ...loginRequest,
        account: accounts[0]
      }

      instance
        .acquireTokenSilent(request)
        .then((response: any) => {
          const tokenParts = response.accessToken.split('.')
          const tokenPayload = JSON.parse(window.atob(tokenParts[1]))
          const email = response?.account?.username
          const name = response?.account?.name
          ai.setAuthenticatedUserContext(email || '')
          dispatch(
            setUserContext({
              accessToken: response.accessToken,
              id: response?.uniqueId,
              name,
              roles: tokenPayload.roles,
              email
            })
          )

          const getUserData = async () => {
            const url = `${mapper.bff.getUserDetails}`
            const profileUrl = `${mapper.bff.getUseProfile}`
            const [userData, profileData] = await Promise.all([
              getUserDetailsData(url),
              getProfileUserData(profileUrl)
            ])
            const userInfoData = {
              ...userData,
              jobFunction: profileData?.jobFunction
            }
            dispatch({ type: 'SET_PROFILE', payload: userInfoData })

            localStorage.setItem('userInfoData', JSON.stringify(userInfoData))
            setIsInInvestmentDesktop(userData?.isInInvestmentDesktop)
            if (!userData?.isInInvestmentDesktop) {
              navigate(DefaultRoute)
            } else if (location.pathname === '/account-info') {
              navigate(MainRoute, { state: { checkProfileCompletion: true } })
            } else {
              navigate({ ...location }, { state: { checkProfileCompletion: true } })
            }

            const workspaceConfig = await getWorkspaceConfig(
              mapper.workspace.getWorkspaceConfig.replace(':key', `${userData.sid}-workspace`)
            )

            if (workspaceConfig?.status === 'Not Found' && workspaces) {
              const data = {
                workspaces: {
                  workspaceList: workspaces?.length ? workspaces : [],
                  activeWorkspace: workspaces?.length ? activeWorkspace : null
                }
              }
              dispatch(setWorkspaceData(data.workspaces))
              await setUpNewWorkspace(workspaces, userData.sid)
            } else if (workspaceConfig?.status === 'success') {
              dispatch(setWorkspaceData(JSON.parse(workspaceConfig.data)))
            }
          }

          getUserData().then(() => {
            connectSignalR()
          })
        })
        .catch((e) => {
          if (e instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(request)
          }
        })
    }
  }, [inProgress, instance, accounts])

  return isRoutesManagerLoading ? (
    <SplashScreen shouldShow={isRoutesManagerLoading} />
  ) : (
    context?.accessToken && isInInvestmentDesktop !== undefined && <Router />
  )
}

export default RoutesManager
